import { TrimEditableImage } from '../image-edit/trim.editable-image';
import { TrimImageProcessorManager } from './trim.image-processor.manager';
import { TrimCjBitmap } from '../../model/cj-factory';
import { TrimCjTool } from '../../../utilities/trim.cj-tool';
import { CJ_DPOBJ_NAME } from '../../model/cj-factory/trim.cj-obj-name.collection';
import { HashGenerator } from '../../../utilities/hash-generator';
import { ApiImagesGetOne } from '../../../api/front/images/api-images';
import { store } from '../../../app/store';
import { apiActions } from '../../../slices/api-slice';
import { Orientation } from 'get-orientation/browser';
import { ApiGetCloudFolderFileDownload } from '../../../api/cf/cloud-folder/api-get-cloud-folder-file-download';
import { CustomError } from '../../../models/custom-error';
import { UuidGenerator } from '../../../utilities/uuid-generator';
import { IMAGES_KIND } from '../../../layout-editor/manager/image-processor/image-processor.manager';
import { normalizeJpegOrientation } from '../../../utilities/jpeg-orientation';

const dummyExif = {
  colorSpace: '',
  model: '',
  make: '',
  createDate: '',
  orientation: 0,
};

export class TrimProcess {
  id: string;
  constructor(
    public original: File | null,
    public kijShopCd: string,
    public shopOrderId: string,
    public orderId: string | null,
    public kind: string, // 完成画像:1, アップロード画像:6
    public onUploaded: (editableImage: TrimEditableImage, sameImage?: boolean) => void = () => {
    },
    public onCreatedEditableImage: (editableImage: TrimEditableImage, sameImage?: boolean) => void = () => {
    },
    public additionalProps: {
      originalName: string,
      name: string,
      exif: { colorSpace: any; model: any; orientation: any; make: any; createDate: any },
      width: number,
      height: number,
      thumbnailUrl: string,
      hash: string,
    } | null,
    public restore: boolean = false,
    public path: string = '',
    public selectId: string = '',
    public isUseCheck: boolean = false,
    public hashData?: {
      create?: boolean,
      hash?: string,
      checkList?: { path: string, hash: string, orientation?: Orientation }[],
      selectIdList?: { path: string, selectID: string }[]
    },
    public samePushIgnore?: boolean,
    public isDelete?: boolean,
    public onError?: () => void,
    public imgId?: string,
  ) {
    this.id = UuidGenerator.create();
  }

  async all() {
    /* 通常の画像作成 */
    const original = await this.makeOriginal();
    // NOTE : オリジナルの Base64 はメモリを圧迫するので保持しない
    // const originalBase64 = await this.makeOriginalBase64();
    const thumbnail = await this.makeThumbnail();
    const thumbnailBase64 = await this.makeThumbnailBase64(thumbnail);
    console.log('- after make thumbnail base64 -');
    const cjImage = await this.makeCjImage(thumbnailBase64);
    const exif = original ? await TrimImageProcessorManager.loadExif(original) : (this.additionalProps?.exif || dummyExif);
    const hash = original ? await HashGenerator.generate(original) : this.additionalProps?.hash;
    const size = await this.makeWidthHeight();
    const editableImage = new TrimEditableImage({
      id: this.imgId,
      name: original ? original.name : (this.additionalProps?.originalName || 'NoName'),
      kijshopCd: this.kijShopCd,
      shopOrderId: this.shopOrderId,
      orderId: this.orderId,
      selectId: this.selectId,
      original,
      kind: this.kind,
      // NOTE : オリジナルの Base64 はメモリを圧迫するので保持しない
      originalBase64: undefined,
      thumbnail,
      thumbnailBase64,
      editable: cjImage,
      exif: { ...exif, createDate: exif.createDate ? new Date(exif.createDate) : null },
      height: String(cjImage.image.height),
      width: String(cjImage.image.width),
      hash,
      realWidth: String(size.w),
      realHeight: String(size.h),
    });
    if (this.restore) {
      editableImage.flags.uploaded = true;
      editableImage.path = this.path;
    }
    if (this.selectId) {
      editableImage.selectId = this.selectId;
    }
    if (this.isUseCheck) {
      editableImage.flags.used = true;
    }
    if (isNaN(exif.orientation)) {
      exif.orientation = 0;
    }
    // this.callback(editableImage);
    return editableImage;
  }

  private async makeOriginal() {
    console.log('[img-prc] make original ...');
    if (!this.original) {
      return null;
    }
    /*
     * まとめてプリントのみ、Exif の回転情報を画素に焼き込んでからアップロードする。
     * this.original を差し替えることで、後続のサムネイル生成・Exif 読み取り・
     * ハッシュ生成・実寸取得がすべて回転後の画像を対象にする。
     * 回転が不要な画像は加工されず元のファイルがそのまま返る。
     */
    if (this.kind === IMAGES_KIND.PRINT) {
      this.original = await normalizeJpegOrientation(this.original);
    }
    return this.original;
  }

  private async makeThumbnail() {
    console.log('[img-prc] make thumbnail ...');
    if (this.original) {
      return TrimImageProcessorManager.resize(this.original);
    }
    if (!this.additionalProps) {
      throw new Error('additionalProps を設定してください !!');
    }
    const blob = await new Promise<Blob>((resolve) => {
      if (!this.additionalProps) {
        throw new Error('additionalProps を設定してください !!');
      }
      store.dispatch(apiActions.run(
        new ApiImagesGetOne({
          kijshopCd: this.kijShopCd,
          path: `${this.additionalProps.thumbnailUrl}/${this.additionalProps.name}`,
        }),
        {
          onSuccess: (blob: Blob) => {
            resolve(blob);
          },
          onError: (blob: Blob) => {
            resolve(blob);
          },
        },
      ));
    });
    return new File(
      [blob],
      this.additionalProps.originalName,
      {
        type: 'image/jpg',
      },
    );
  }

  private makeThumbnailBase64(thumbnail: File) {
    console.log('[img-prc] make thumbnail base64 ...');
    return TrimImageProcessorManager.fileToBase64(thumbnail);
  }

  private makeCjImage(thumbnailUrl: string) {
    console.log('[img-prc] make create.js image ...');
    return new Promise<TrimCjBitmap>((resolve) => {
      const image = new TrimCjBitmap(thumbnailUrl);
      TrimCjTool.checkLoad(image).then((response) => {
        response.name = CJ_DPOBJ_NAME.image;
        resolve(response);
      });
    });
  }

  private makeWidthHeight() {
    console.log('[img-prc] make width/height ...');
    if (this.original) {
      return TrimImageProcessorManager.getImageSize(this.original);
    }
    if (!this.additionalProps) {
      throw new Error('additionalProps を設定してください !!');
    }
    return { w: this.additionalProps.width, h: this.additionalProps.height };
  }

  /* CloudFolder連携 */
  private makeThumbnailCloudFolder() {
    if (!this.original) {
      throw new Error('original を設定してください !!');
    }
    return this.original;
  }

  private makeEditableInfoCloudFolder() {
    if (!this.additionalProps) {
      throw new Error('additionalProps を設定してください !!');
    }
    const exif = this.makeExifCloudFolder();
    const hash = this.makeHashCloudFolder();
    const size = this.makeSizeCloudFolder();
    return {
      exif,
      hash,
      ...size,
    };
  }

  private makeExifCloudFolder() {
    if (!this.additionalProps?.exif) {
      return {
        createDate: new Date(),
        colorSpace: '',
        make: '',
        model: '',
        width: 0,
        height: 0,
        orientation: 0,
      };
    } else {
      return this.additionalProps.exif;
    }
  }

  private makeHashCloudFolder() {
    if (!this.hashData?.hash) {
      throw new Error('hashData.hash を設定してください !!');
    } else {
      return this.hashData.hash;
    }
  }

  private makeSizeCloudFolder() {
    if (!this.additionalProps) {
      throw new Error('additionalProps を設定してください !!');
    } else {
      return {
        realWidth: `${this.additionalProps.width}`,
        realHeight: `${this.additionalProps.height}`,
      };
    }
  }

}
